import React, { useState, useEffect } from 'react';
import {
  getRentalSettings, saveRentalSettings, getRentals, getActiveRental,
  getDrafts, createDraft, activateRental,
  extendRental, closeRental, deleteRental, updateRental, rentalMath,
  rentalsInRange, summarizeRevenue, DAY_MS, odoMilesFromAttrs,
  isOnDoNotRent, addDoNotRent,
} from './rentalStore';
import { getRouteReport, getSummaryReport, getAllDevices, updateDevice } from './traccar';
import { estimateTolls } from './tollEstimate';
import SignaturePad from './SignaturePad';
import { contractPlainText, liabilityClauses } from './rentalContract';
import { openRentalPdf } from './rentalPdf';
import { getVehiclePhotos, getRentalFile, saveRentalFile, saveRentalPhoto, getRentalPhotos, fileToResizedDataUrl } from './photoStore';
import PhotoPicker from './PhotoPicker';
import { sendEmail, receiptEmail, contractEmail, emailBackendReady } from './emailer';

// Set (or clear) the car's assigned driver on its device profile, so it shows on
// the vehicle profile and the chat can answer "who's driving this car".
async function setAssignedDriver(deviceId, name, license) {
  try {
    const all = await getAllDevices();
    const dev = all.find((d) => String(d.id) === String(deviceId));
    if (dev) await updateDevice({ ...dev, attributes: { ...(dev.attributes || {}), assignedDriver: name || '', assignedDriverLicense: license || '' } });
  } catch { /* ignore — assignment is best-effort */ }
}

const METERS_PER_MILE = 1609.344;

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const inp = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px' };
const lbl = { fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '3px' };
const btn = (bg, c) => ({ background: bg, color: c, border: 'none', borderRadius: '8px', padding: '8px 14px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' });

// Rental management for one vehicle. odometerMi = current odometer (miles) if the
// tracker reports it — used to auto-fill start/current mileage.
export default function RentalPanel({ deviceId, odometerMi, vehicle, actor }) {
  const veh = vehicle || {};
  const vehicleLabel = [veh.year, veh.make, veh.model].filter(Boolean).join(' ') || veh.name || 'Vehicle';
  const vehicleFull = vehicleLabel + (veh.plate ? ` · ${veh.plate}` : '');
  const [, force] = useState(0);
  const refresh = () => force((n) => n + 1);
  const settings = getRentalSettings(deviceId);
  const active = getActiveRental(deviceId, actor);
  const drafts = getDrafts(deviceId, actor);
  const rentals = getRentals(deviceId, actor);
  const liveOdo = odometerMi != null ? Math.round(odometerMi) : ''; // real OBD odometer

  const [tab, setTab] = useState('rental'); // 'rental' | 'history' | 'calendar' | 'reports'
  const [editingDraftId, setEditingDraftId] = useState(null); // draft being prepared
  const [viewRentalId, setViewRentalId] = useState(null); // rental whose agreement is popped up
  const [endOdo, setEndOdo] = useState(odometerMi != null ? Math.round(odometerMi) : '');
  const [ext, setExt] = useState({ days: 1, dailyPrice: '' });
  const [charge, setCharge] = useState(''); // amount to request via payment link
  const [chargeReason, setChargeReason] = useState(''); // note shown as the charge reason
  const [closing, setClosing] = useState(false); // close-out flow open
  const [depReturned, setDepReturned] = useState(''); // deposit returned at close
  const [depReason, setDepReason] = useState(''); // reason if not fully returned
  const [closeCash, setCloseCash] = useState(false); // balance collected in cash at return
  const [closeDnr, setCloseDnr] = useState(false); // add renter to Do Not Rent on close
  const [closeDnrReason, setCloseDnrReason] = useState(''); // reason for the flag
  const [liveMiles, setLiveMiles] = useState(null); // GPS miles driven this rental
  const [tollBusy, setTollBusy] = useState(false); // toll estimate running

  const runTollEstimate = async (r) => {
    setTollBusy(true);
    try {
      const rate = r.tollRate != null ? Number(r.tollRate) : 0.5;
      const res = await estimateTolls(deviceId, r.startAt, r.status === 'closed' ? r.closedAt : new Date().toISOString(), rate);
      updateRental(r.id, { tolls: res.list, tollEst: res.estCost, tollSampledAt: new Date().toISOString() });
      refresh();
    } catch { alert('Could not estimate tolls right now.'); }
    setTollBusy(false);
  };
  const [, tick] = useState(0); // 1s tick for the live countdown

  // tick every second so the countdown clock updates live
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [active]);

  // real miles driven since the rental started = GPS distance over the period
  // (works even when the OBD odometer is static). Refreshes with the vehicle.
  useEffect(() => {
    let cancel = false;
    if (!active) { setLiveMiles(null); return undefined; }
    getSummaryReport(deviceId, active.startAt, new Date().toISOString())
      .then((s) => { if (!cancel) setLiveMiles(s && s.distance != null ? s.distance / METERS_PER_MILE : null); })
      .catch(() => {});
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, active && active.id, active && active.startAt, odometerMi]);
  const [calMonth, setCalMonth] = useState(new Date());

  const toggleEnabled = () => { saveRentalSettings(deviceId, { enabled: !settings.enabled }); refresh(); };
  const setS = (patch) => { saveRentalSettings(deviceId, patch); refresh(); };

  if (!settings.enabled) {
    return (
      <div style={{ padding: '12px', background: '#e2e8f0', border: '1px dashed #cbd5e1', borderRadius: '10px' }}>
        <div style={{ fontWeight: 700, fontSize: '13px', color: '#0f172a', marginBottom: '4px' }}>🚙 Rental vehicle</div>
        <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '10px' }}>Turn this on if you rent this car out. You'll get a rental timer, mileage limits, overage charges, revenue, and a rental calendar.</div>
        <button onClick={toggleEnabled} style={btn('linear-gradient(135deg,#0066ff,#0047d0)', 'white')}>Enable rental mode</button>
      </div>
    );
  }

  // itemized charge breakdown for a rental
  const chargeBreakdown = (r, m) => {
    const rows = [[`${r.days} day${r.days === 1 ? '' : 's'} × ${money(r.dailyPrice)}`, r.days * r.dailyPrice]];
    (r.extensions || []).forEach((e) => rows.push([`Extension: ${e.days} day${e.days === 1 ? '' : 's'} × ${money(e.dailyPrice)}`, e.days * e.dailyPrice]));
    if (m.overageCharge) rows.push([`Overage: ${Math.round(m.overageMiles)} mi × ${money(r.overageRate)}`, m.overageCharge]);
    if (m.tollCharge) rows.push([`Tolls: ${m.tollCount} × ${money(m.tollRate)}`, m.tollCharge]);
    rows.push(['Subtotal', m.total, 'sub']);
    if (m.paid) rows.push(['Paid', -m.paid]);
    rows.push(['Balance due', m.balanceDue, 'due']);
    return rows;
  };

  // Emails the renter an invoice (vehicle, dates, miles, breakdown) with the
  // payment link. External card processing connects later.
  const sendPaymentLink = (rental, amount, reason, m) => {
    const amt = Math.max(0, Math.round(Number(amount) || 0));
    if (!amt) { alert('Enter an amount to charge first.'); return; }
    updateRental(rental.id, { chargeReason: reason || '' }); refresh();
    const link = `${window.location.origin}/pay/${rental.id}?amount=${amt}`;
    const lines = chargeBreakdown(rental, m).map(([label, val]) => `  ${label}: ${money(val)}`).join('\n');
    const dates = `${new Date(rental.startAt).toLocaleDateString()} → ${new Date(m.dueAt).toLocaleDateString()} (${m.tDays} day${m.tDays === 1 ? '' : 's'})`;
    const milesTxt = m.milesDriven != null ? `${Math.round(m.milesDriven)} of ${m.allowedMiles} mi${m.overageMiles ? ` (${Math.round(m.overageMiles)} over)` : ''}` : `${m.allowedMiles} mi allowance`;
    const body = `Hi ${rental.renter || ''},

Here is your rental invoice.

Vehicle: ${vehicleFull}
Rental period: ${dates}
Mileage: ${milesTxt}
${reason ? `\nNote: ${reason}\n` : ''}
Charges:
${lines}

Amount due: ${money(amt)}

Pay securely here: ${link}

Thank you.`;
    const subject = `Rental invoice — ${money(amt)} due`;
    try { if (navigator.clipboard) navigator.clipboard.writeText(link); } catch { /* ignore */ }
    const email = rental.email || window.prompt("Renter's email to send the invoice to:", '');
    if (!email) { alert(`No email entered. Payment link copied:\n${link}`); return; }
    if (!rental.email) { updateRental(rental.id, { email }); refresh(); }
    sendEmail({ to: email, subject, body });
  };

  // Email a payment receipt for what's been paid so far.
  const emailReceipt = (rental, m) => {
    const email = rental.email || window.prompt("Renter's email to send the receipt to:", '');
    if (!email) return;
    if (!rental.email) { updateRental(rental.id, { email }); refresh(); }
    const lastPay = (rental.payments || [])[rental.payments.length - 1];
    const tmpl = receiptEmail({
      renter: rental.renter, vehicle: vehicleFull,
      amount: money(m.paid), method: lastPay ? lastPay.method : '',
      dateStr: new Date().toLocaleDateString(),
      balance: m.balanceDue > 0 ? money(m.balanceDue) : '',
    });
    sendEmail({ to: email, ...tmpl });
  };

  // Email the rental agreement to the renter to review & sign.
  const emailContract = (rental) => {
    const email = rental.email || window.prompt("Renter's email to send the contract to:", '');
    if (!email) return;
    if (!rental.email) { updateRental(rental.id, { email }); refresh(); }
    const text = rental.contractSnapshot || contractPlainText({ ...rental, vehicle: vehicleFull });
    const tmpl = contractEmail({ renter: rental.renter, vehicle: vehicleFull, contractText: text, signLink: '' });
    sendEmail({ to: email, ...tmpl });
  };

  // Record a CASH payment — adds the amount to what's been paid, with a note + log.
  const markPaidCash = (rental, amount, reason) => {
    const amt = Math.max(0, Math.round(Number(amount) || 0));
    if (!amt) { alert('Enter an amount to mark as paid in cash.'); return; }
    const payments = [...(rental.payments || []), { amount: amt, method: 'cash', at: new Date().toISOString(), note: reason || '' }];
    updateRental(rental.id, { paid: (Number(rental.paid) || 0) + amt, payments, chargeReason: reason || rental.chargeReason || '' });
    setCharge(''); refresh();
    alert(`💵 Recorded ${money(amt)} paid in cash.`);
  };

  const downloadCsv = (name, rows) => {
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
  };
  const runReport = (period) => {
    const to = new Date();
    const from = new Date(to.getTime() - (period === 'daily' ? DAY_MS : period === 'weekly' ? 7 * DAY_MS : 30 * DAY_MS));
    const list = rentalsInRange(deviceId, from, to, actor);
    const sum = summarizeRevenue(list);
    const rows = [['Renter', 'Start', 'Days', 'Miles', 'Base', 'Overage', 'Total', 'Status']];
    list.forEach((r) => { const m = rentalMath(r, r.endOdoMi ?? odometerMi); rows.push([r.renter, new Date(r.startAt).toLocaleDateString(), m.tDays, m.milesDriven ?? '', money(m.base), money(m.overageCharge), money(m.total), r.status]); });
    rows.push([]); rows.push(['TOTALS', `${sum.count} rentals`, sum.days, Math.round(sum.miles), '', '', money(sum.revenue), '']);
    downloadCsv(`rentals-${period}-${to.toISOString().slice(0, 10)}.csv`, rows);
  };

  // assemble the agreement/PDF context for a rental
  const buildCtx = (r, m) => ({
    company: 'TagAlong Rental',
    vehicle: vehicleLabel,
    vin: veh.vin || '',
    plate: veh.plate || '',
    renter: r.renter, license: r.renterLicense || '', phone: r.phone, email: r.email,
    startAt: r.startAt, dueAt: m.dueAt, days: m.tDays,
    // actual time the car was out (may exceed the booked term if the renter kept it longer)
    status: r.status, returnedAt: r.closedAt || null,
    actualDays: Math.max(Number(m.tDays) || 0, Math.ceil(m.daysElapsed || 0)),
    dailyPrice: r.dailyPrice, includedMilesPerDay: r.includedMilesPerDay, overageRate: r.overageRate,
    deposit: r.deposit, startOdoMi: r.startOdoMi,
  });

  // open the print-ready rental document (all info + license + signature + car photos)
  const printPdf = async (r) => {
    const m = rentalMath(r, r.status === 'closed' ? r.endOdoMi : odometerMi, r.status === 'active' ? liveMiles : undefined);
    let carPhotos = {}; let licensePhoto = null; let signature = null; let customerPhoto = null;
    try {
      const [vp, rp, lic, cust, sig] = await Promise.all([
        getVehiclePhotos(deviceId).catch(() => ({})),
        getRentalPhotos(r.id).catch(() => ({})),
        getRentalFile(r.id, 'license').catch(() => null),
        getRentalFile(r.id, 'customer').catch(() => null),
        getRentalFile(r.id, 'signature').catch(() => null),
      ]);
      // this rental's own photos take priority; fall back to the car profile photos
      carPhotos = { ...vp };
      Object.keys(rp || {}).forEach((s) => { if (rp[s]) carPhotos[s] = rp[s]; });
      licensePhoto = lic; signature = sig; customerPhoto = cust;
    } catch { /* ignore */ }
    openRentalPdf({ company: 'TagAlong Rental', ctx: buildCtx(r, m), totals: m, rental: r, licensePhoto, customerPhoto, signature, signedAt: r.signedAt, carPhotos });
  };

  return (
    <div style={{ padding: '12px', background: '#e2e8f0', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
        <div style={{ fontWeight: 800, fontSize: '14px', color: '#0f172a', flex: 1 }}>🚙 Rental</div>
        {['rental', 'history', 'calendar', 'reports'].map((t) => (
          <button key={t} onClick={() => setTab(t)} style={{ ...btn(tab === t ? '#0f172a' : 'white', tab === t ? 'white' : '#334155'), border: tab === t ? 'none' : '1px solid #cbd5e1', padding: '5px 12px', fontSize: '12px', textTransform: 'capitalize' }}>{t}</button>
        ))}
        <button onClick={toggleEnabled} title="Turn off rental mode" style={{ ...btn('transparent', '#94a3b8'), padding: '5px 8px', fontSize: '11px' }}>Disable</button>
      </div>

      {tab === 'rental' && (
        <>
          {/* default pricing */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '8px' }}>
            <div><span style={lbl}>Daily price</span><input style={inp} type="number" value={settings.dailyPrice} onChange={(e) => setS({ dailyPrice: e.target.value })} /></div>
            <div><span style={lbl}>Incl. miles/day</span><input style={inp} type="number" value={settings.includedMilesPerDay} onChange={(e) => setS({ includedMilesPerDay: e.target.value })} /></div>
            <div><span style={lbl}>Overage $/mi</span><input style={inp} type="number" step="0.01" value={settings.overageRate} onChange={(e) => setS({ overageRate: e.target.value })} /></div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '12px', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!settings.dailyReport} onChange={(e) => setS({ dailyReport: e.target.checked })} />
            📅 End-of-day miles report (an alert each morning with yesterday's miles)
          </label>

          {active ? (() => {
            const m = rentalMath(active, odometerMi, liveMiles);
            return (
              <div style={{ background: '#eef2f7', border: `1px solid ${m.overdue ? '#fecaca' : '#bfdbfe'}`, borderRadius: '10px', padding: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontWeight: 800, fontSize: '15px', color: '#0f172a' }}>{active.renter}</span>
                  {active.phone && <a href={`tel:${active.phone}`} style={{ fontSize: '12px', color: '#1d4ed8', textDecoration: 'none' }}>📞 {active.phone}</a>}
                  <span style={{ marginLeft: 'auto', fontSize: '11px', fontWeight: 800, padding: '3px 10px', borderRadius: '999px', background: m.overdue ? '#fef2f2' : '#eff6ff', color: m.overdue ? '#b91c1c' : '#1d4ed8' }}>
                    {m.overdue ? 'OVERDUE' : 'ACTIVE'}
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: '#334155', marginBottom: '4px' }}>🚗 {vehicleFull}</div>
                <div style={{ fontSize: '12px', color: '#334155', fontWeight: 600, marginBottom: '4px' }}>
                  📅 Started {new Date(active.startAt).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })} at {new Date(active.startAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · due {new Date(m.dueAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                </div>
                <div style={{ fontSize: '12px', color: '#334155', marginBottom: '6px' }}>🛣️ {m.milesDriven != null ? `${Math.round(m.milesDriven)} of ${m.allowedMiles} mi driven${m.overageMiles ? ` · ${Math.round(m.overageMiles)} over` : ''}` : `${m.allowedMiles} mi allowance`}</div>
                <div style={{ fontSize: '12px', color: '#334155', marginBottom: '10px' }}>
                  🚧 Tolls: {m.tollCount ? <b>{money(m.tollCharge)}</b> : '—'}{m.tollCount ? ` · ${m.tollCount} × ${money(m.tollRate)}` : ''}{' '}
                  <button onClick={() => runTollEstimate(active)} disabled={tollBusy} style={{ background: 'transparent', border: 'none', color: '#1d4ed8', cursor: 'pointer', fontSize: '11px', fontWeight: 700, padding: 0 }}>{tollBusy ? 'estimating…' : 'update estimate ▸'}</button>
                  {active.tolls && active.tolls.length > 0 && (
                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{active.tolls.map((t, i) => <div key={i}>• {t.road} — {new Date(t.at).toLocaleString()}</div>)}</div>
                  )}
                  <div style={{ fontSize: '10px', color: '#94a3b8' }}>Estimate from GPS + road data (a few seconds to scan the route). Not the exact toll charge.</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '8px', marginBottom: '10px' }}>
                  <Metric label={m.overdue ? 'Overdue by' : 'Time left'} value={fmtCountdown(m.overdue ? -m.msLeft : m.msLeft)} color={m.overdue ? '#b91c1c' : '#0f172a'} />
                  <Metric label="Miles" value={m.milesDriven != null ? `${Math.round(m.milesDriven)} / ${m.allowedMiles}` : '—'} color={m.overMiles ? '#b45309' : '#0f172a'} />
                  <Metric label="Overage" value={m.overageMiles ? `${Math.round(m.overageMiles)} mi · ${money(m.overageCharge)}` : '—'} color={m.overageMiles ? '#b45309' : '#0f172a'} />
                  <Metric label="Running total" value={money(m.total)} color="#16a34a" />
                </div>
                <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '10px' }}>
                  Started {new Date(active.startAt).toLocaleString()} · due {new Date(m.dueAt).toLocaleString()} · {m.tDays} day{m.tDays === 1 ? '' : 's'} · base {money(m.base)}
                  {' · '}
                  <button onClick={() => { const v = window.prompt('Start odometer (miles) — the reading when this rental began:', active.startOdoMi != null ? active.startOdoMi : ''); if (v != null && v !== '') { updateRental(active.id, { startOdoMi: Number(v) }); refresh(); } }} style={{ background: 'transparent', border: 'none', color: '#1d4ed8', cursor: 'pointer', padding: 0, fontSize: '11px', fontWeight: 700 }}>
                    start odo {active.startOdoMi != null ? `${active.startOdoMi} mi` : '(set)'} ✏️
                  </button>
                </div>
                {/* payment */}
                <div style={{ background: '#e2e8f0', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '8px' }}>
                    <div style={{ width: '110px' }}><span style={lbl}>Paid so far ($)</span><input style={inp} type="number" value={active.paid || 0} onChange={(e) => { updateRental(active.id, { paid: Number(e.target.value) || 0 }); refresh(); }} /></div>
                    <div style={{ flex: 1, minWidth: '150px', fontSize: '12px', color: '#334155' }}>
                      {m.balanceDue > 0
                        ? <span style={{ color: '#b45309', fontWeight: 800 }}>Balance due {money(m.balanceDue)}</span>
                        : <span style={{ color: '#16a34a', fontWeight: 800 }}>Paid in full{m.prepaidLeft ? ` · credit ${money(m.prepaidLeft)}` : ''}</span>}
                    </div>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <span style={lbl}>Reason for balance (optional — shows on the invoice)</span>
                    <textarea style={{ ...inp, minHeight: '40px' }} value={chargeReason !== '' ? chargeReason : (active.chargeReason || '')} onChange={(e) => setChargeReason(e.target.value)} placeholder="e.g. 2 extra days + 40 miles over the limit" />
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div style={{ width: '100px' }}><span style={lbl}>Amount ($)</span><input style={inp} type="number" value={charge !== '' ? charge : Math.round(m.balanceDue)} onChange={(e) => setCharge(e.target.value)} /></div>
                    <div style={{ flex: 1, minWidth: '170px' }}><span style={lbl}>Renter email</span><input style={inp} type="email" value={active.email || ''} onChange={(e) => { updateRental(active.id, { email: e.target.value }); refresh(); }} placeholder="renter@email.com" /></div>
                    <button onClick={() => sendPaymentLink(active, charge !== '' ? charge : Math.round(m.balanceDue), chargeReason !== '' ? chargeReason : (active.chargeReason || ''), m)} style={btn('#7c3aed', 'white')}>💳 Send payment link</button>
                    <button onClick={() => markPaidCash(active, charge !== '' ? charge : Math.round(m.balanceDue), chargeReason !== '' ? chargeReason : (active.chargeReason || ''))} style={btn('#16a34a', 'white')}>💵 Mark paid (cash)</button>
                    {(active.paid || 0) > 0 && <button onClick={() => emailReceipt(active, m)} style={btn('#0891b2', 'white')}>🧾 Email receipt</button>}
                    <button onClick={() => emailContract(active)} style={btn('#334155', 'white')}>📧 Email contract</button>
                  </div>
                  <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '6px' }}>Send the invoice + pay link, email a receipt or the contract to sign, or record a cash payment. {emailBackendReady() ? 'Emails send automatically.' : 'Emails open in your mail app pre-filled.'} Card processing when the gateway is connected.</div>
                </div>

                {/* extend */}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginBottom: '8px', flexWrap: 'wrap' }}>
                  <div style={{ width: '90px' }}><span style={lbl}>Extend days</span><input style={inp} type="number" value={ext.days} onChange={(e) => setExt({ ...ext, days: e.target.value })} /></div>
                  <div style={{ width: '120px' }}><span style={lbl}>New $/day (opt)</span><input style={inp} type="number" placeholder={String(active.dailyPrice)} value={ext.dailyPrice} onChange={(e) => setExt({ ...ext, dailyPrice: e.target.value })} /></div>
                  <button onClick={() => { extendRental(active.id, ext.days, ext.dailyPrice === '' ? undefined : ext.dailyPrice); setExt({ days: 1, dailyPrice: '' }); refresh(); }} style={btn('#eef2ff', '#1d4ed8')}>➕ Extend</button>
                </div>

                {/* rental agreement — license, contract, signature, PDF */}
                <AgreementBlock rental={active} deviceId={deviceId} ctx={buildCtx(active, m)} onPrint={() => printPdf(active)} />

                {/* close-out */}
                {!closing ? (
                  <button onClick={() => { setClosing(true); setDepReturned(active.deposit != null ? active.deposit : ''); setDepReason(''); setCloseCash(false); setCloseDnr(false); setCloseDnrReason(''); }} style={btn('#16a34a', 'white')}>✓ End rental & close out</button>
                ) : (
                  <div style={{ background: '#eef2f7', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '12px' }}>
                    <div style={{ fontWeight: 800, fontSize: '13px', marginBottom: '8px' }}>Close out rental</div>
                    <div style={{ fontSize: '12px', color: '#334155', marginBottom: '8px' }}>
                      Total {money(m.total)} · paid {money(m.paid)} · {m.balanceDue > 0 ? <b style={{ color: '#b45309' }}>balance due {money(m.balanceDue)}</b> : <b style={{ color: '#16a34a' }}>paid in full</b>}
                      {liveOdo !== '' && <> · 📟 return odo {liveOdo.toLocaleString()} mi</>}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '8px' }}>
                      <div style={{ width: '150px' }}><span style={lbl}>Deposit returned ($)</span><input style={inp} type="number" value={depReturned} onChange={(e) => setDepReturned(e.target.value)} /></div>
                      {liveOdo === '' && <div style={{ width: '140px' }}><span style={lbl}>End odometer (mi)</span><input style={inp} type="number" value={endOdo} onChange={(e) => setEndOdo(e.target.value)} /></div>}
                    </div>
                    {(active.deposit || 0) > 0 && Number(depReturned || 0) < (active.deposit || 0) && (
                      <div style={{ marginBottom: '8px' }}>
                        <span style={lbl}>Reason deposit not fully returned</span>
                        <textarea style={{ ...inp, minHeight: '48px' }} value={depReason} onChange={(e) => setDepReason(e.target.value)} placeholder="e.g. $75 kept for a scratch on the rear bumper / returned late / cleaning fee…" />
                      </div>
                    )}
                    {m.balanceDue > 0 && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '8px', cursor: 'pointer' }}>
                        <input type="checkbox" checked={closeCash} onChange={(e) => setCloseCash(e.target.checked)} />
                        💵 Balance of {money(m.balanceDue)} collected in cash at return (marks it paid)
                      </label>
                    )}
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: 600, color: '#b91c1c', marginBottom: '8px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={closeDnr} onChange={(e) => setCloseDnr(e.target.checked)} />
                      🚫 Add {active.renter || 'this renter'} to the Do Not Rent list
                    </label>
                    {closeDnr && (
                      <div style={{ marginBottom: '8px' }}>
                        <span style={lbl}>Reason for Do Not Rent (optional)</span>
                        <textarea style={{ ...inp, minHeight: '40px' }} value={closeDnrReason} onChange={(e) => setCloseDnrReason(e.target.value)} placeholder="e.g. returned the car damaged / unpaid balance / smoking in vehicle" />
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => setClosing(false)} style={btn('#e2e8f0', '#334155')}>Cancel</button>
                      <button onClick={() => {
                        const withheld = (active.deposit || 0) - Number(depReturned || 0);
                        if (withheld > 0 && !depReason.trim()) { alert('Add a reason for keeping part of the deposit.'); return; }
                        const patch = { milesDriven: liveMiles != null ? Math.round(liveMiles) : undefined, depositReturned: depReturned === '' ? 0 : Number(depReturned), depositReason: depReason };
                        if (closeCash && m.balanceDue > 0) {
                          const bal = Math.round(m.balanceDue);
                          patch.paid = (Number(active.paid) || 0) + bal;
                          patch.payments = [...(active.payments || []), { amount: bal, method: 'cash', at: new Date().toISOString(), note: 'Balance collected in cash at return' }];
                        }
                        if (closeDnr) {
                          const lic = (active.renterLicense || '').trim() || window.prompt("Driver's license # to add to Do Not Rent:", '');
                          if (lic) addDoNotRent({ license: lic, name: active.renter || '', reason: closeDnrReason });
                          else alert('No license number — skipped the Do Not Rent add.');
                        }
                        updateRental(active.id, patch);
                        closeRental(active.id, liveOdo !== '' ? liveOdo : (endOdo === '' ? null : endOdo));
                        setAssignedDriver(deviceId, '', ''); // rental over — unassign the driver
                        setClosing(false); setCloseCash(false); setCloseDnr(false); setCloseDnrReason(''); refresh();
                      }} style={btn('#16a34a', 'white')}>✓ Confirm close-out</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })() : editingDraftId ? (
            <RentalSetup
              deviceId={deviceId} draftId={editingDraftId} liveOdo={liveOdo} odometerMi={odometerMi}
              buildCtx={buildCtx} printPdf={printPdf} sendPaymentLink={sendPaymentLink}
              onClose={() => { setEditingDraftId(null); refresh(); }}
            />
          ) : (
            <div style={{ background: '#eef2f7', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <div style={{ fontWeight: 700, fontSize: '13px', flex: 1 }}>Start a new rental</div>
                <button onClick={() => { const d = createDraft(deviceId, actor); setEditingDraftId(d.id); }} style={btn('linear-gradient(135deg,#0066ff,#0047d0)', 'white')}>＋ New rental</button>
              </div>
              {drafts.length > 0 ? (
                <>
                  <div style={{ ...lbl, marginBottom: '6px' }}>Drafts — prepared, clock not started</div>
                  {drafts.map((d) => (
                    <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', padding: '8px 10px', background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: '8px', marginBottom: '5px' }}>
                      <span style={{ fontWeight: 800, color: '#0f172a' }}>{d.renter || 'Untitled draft'}</span>
                      <span style={{ color: '#94a3b8' }}>{d.days || 0}d · {money(d.dailyPrice)}/day{d.signedAt ? ' · ✅ signed' : ''}</span>
                      <button onClick={() => setEditingDraftId(d.id)} style={{ ...btn('#7c3aed', 'white'), marginLeft: 'auto', padding: '4px 10px', fontSize: '11px' }}>Open</button>
                      <button onClick={() => { if (window.confirm('Discard this draft?')) { deleteRental(d.id); refresh(); } }} style={{ ...btn('transparent', '#cbd5e1'), padding: '4px 6px' }}>✕</button>
                    </div>
                  ))}
                </>
              ) : (
                <div style={{ fontSize: '12px', color: '#64748b' }}>No active rental. Click <b>＋ New rental</b> to set one up — enter the renter's info, capture their license and photos, and have them sign. Then <b>Start rental</b> begins the clock, or <b>Save draft</b> to finish at pickup.</div>
              )}
            </div>
          )}

          {/* history — this vehicle's past rentals; click one to open its agreement */}
          {rentals.filter((r) => r.status === 'closed').length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ ...lbl, marginBottom: '6px' }}>Past rentals · {vehicleLabel}</div>
              {rentals.filter((r) => r.status === 'closed').slice(0, 8).map((r) => {
                const m = rentalMath(r, r.endOdoMi);
                return (
                  <div key={r.id} onClick={() => setViewRentalId(r.id)} title="Open the rental agreement" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', padding: '7px 9px', background: '#eef2f7', border: '1px solid #eef2f7', borderRadius: '8px', marginBottom: '4px', cursor: 'pointer' }}>
                    <span style={{ fontWeight: 700, color: '#0f172a' }}>{r.renter || 'Rental'}</span>
                    {r.signedAt && <span title="Signed agreement" style={{ color: '#16a34a' }}>✅</span>}
                    <span style={{ color: '#94a3b8' }}>{new Date(r.startAt).toLocaleDateString()} · {m.tDays}d · {m.milesDriven != null ? `${Math.round(m.milesDriven)}mi` : '—'}</span>
                    <span style={{ marginLeft: 'auto', fontWeight: 800, color: '#16a34a' }}>{money(m.total)}</span>
                    <button onClick={(e) => { e.stopPropagation(); if (window.confirm('Delete this rental record?')) { deleteRental(r.id); refresh(); } }} style={{ ...btn('transparent', '#cbd5e1'), padding: '2px 6px' }}>✕</button>
                  </div>
                );
              })}
              <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>Tap a rental to open its agreement (license, signature, photos, PDF).</div>
            </div>
          )}
        </>
      )}

      {tab === 'history' && <RentalHistory deviceId={deviceId} odometerMi={odometerMi} liveMiles={liveMiles} onPrint={printPdf} buildCtx={buildCtx} actor={actor} />}

      {tab === 'calendar' && <RentalCalendar deviceId={deviceId} month={calMonth} setMonth={setCalMonth} odometerMi={odometerMi} actor={actor} />}

      {tab === 'reports' && (
        <div style={{ background: '#eef2f7', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px' }}>
          <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '8px' }}>Export rental report (CSV)</div>
          {['daily', 'weekly', 'monthly'].map((p) => {
            const to = new Date(); const from = new Date(to.getTime() - (p === 'daily' ? DAY_MS : p === 'weekly' ? 7 * DAY_MS : 30 * DAY_MS));
            const sum = summarizeRevenue(rentalsInRange(deviceId, from, to, actor));
            return (
              <div key={p} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ textTransform: 'capitalize', fontWeight: 700, fontSize: '13px', width: '80px' }}>{p}</span>
                <span style={{ fontSize: '12px', color: '#64748b', flex: 1 }}>{sum.count} rentals · {Math.round(sum.miles)} mi · <b style={{ color: '#16a34a' }}>{money(sum.revenue)}</b></span>
                <button onClick={() => runReport(p)} style={btn('#eef2ff', '#1d4ed8')}>⬇ CSV</button>
              </div>
            );
          })}
        </div>
      )}

      {/* agreement pop-up — opened by clicking a past rental */}
      {viewRentalId && (() => {
        const r = rentals.find((x) => x.id === viewRentalId);
        if (!r) return null;
        const mm = rentalMath(r, r.status === 'closed' ? r.endOdoMi : odometerMi, r.status === 'active' ? liveMiles : undefined);
        return (
          <div onClick={() => setViewRentalId(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 3000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px', overflowY: 'auto' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: '#eef2f7', borderRadius: '12px', width: 'min(580px, 94vw)', padding: '16px', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <div style={{ fontWeight: 800, fontSize: '15px', color: '#0f172a', flex: 1 }}>📝 {r.renter || 'Rental'} · {new Date(r.startAt).toLocaleDateString()}</div>
                <button onClick={() => setViewRentalId(null)} style={{ background: '#f1f5f9', border: 'none', borderRadius: '50%', width: '28px', height: '28px', cursor: 'pointer', fontSize: '16px', color: '#475569' }}>×</button>
              </div>
              <div style={{ fontSize: '12px', color: '#334155', marginBottom: '10px' }}>{vehicleFull} · {mm.tDays}d · {money(mm.total)} · {r.status}</div>
              <AgreementBlock rental={r} deviceId={deviceId} ctx={buildCtx(r, mm)} onPrint={() => printPdf(r)} />
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function Metric({ label, value, color }) {
  return (
    <div style={{ background: '#e2e8f0', borderRadius: '8px', padding: '8px', textAlign: 'center' }}>
      <div style={{ fontSize: '9px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
      <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: '13px', color: color || '#0f172a' }}>{value}</div>
    </div>
  );
}

function fmtLeft(ms) {
  const d = Math.floor(ms / DAY_MS);
  const h = Math.floor((ms % DAY_MS) / (60 * 60 * 1000));
  return d > 0 ? `${d}d ${h}h` : `${h}h`;
}
// live countdown with days + HH:MM:SS
function fmtCountdown(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const p = (n) => String(n).padStart(2, '0');
  return d > 0 ? `${d}d ${p(h)}:${p(m)}:${p(s)}` : `${p(h)}:${p(m)}:${p(s)}`;
}

function RentalCalendar({ deviceId, month, setMonth, odometerMi, actor }) {
  const [sel, setSel] = useState(null);
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const rentals = getRentals(deviceId, actor);
  const onDay = (d) => {
    const day = new Date(month.getFullYear(), month.getMonth(), d);
    const dayEnd = day.getTime() + DAY_MS;
    return rentals.filter((r) => {
      const s = new Date(r.startAt).getTime();
      const m = rentalMath(r, r.endOdoMi ?? odometerMi);
      return s < dayEnd && m.dueAt >= day.getTime();
    });
  };
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div style={{ background: '#eef2f7', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
        <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} style={btn('#f1f5f9', '#334155')}>‹</button>
        <div style={{ flex: 1, textAlign: 'center', fontWeight: 800, fontFamily: 'Poppins, sans-serif' }}>{month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</div>
        <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} style={btn('#f1f5f9', '#334155')}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '3px' }}>
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((w, i) => <div key={i} style={{ textAlign: 'center', fontSize: '10px', fontWeight: 700, color: '#94a3b8' }}>{w}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const list = onDay(d);
          const busy = list.length > 0;
          return (
            <button key={i} onClick={() => setSel(busy ? { d, list } : null)} style={{
              aspectRatio: '1', border: '1px solid #eef2f7', borderRadius: '6px', cursor: busy ? 'pointer' : 'default',
              background: busy ? 'linear-gradient(135deg,#dbeafe,#bfdbfe)' : 'white', fontSize: '11px', fontWeight: busy ? 800 : 500, color: '#0f172a',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            }}>
              {d}{busy && <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#1d4ed8' }} />}
            </button>
          );
        })}
      </div>
      {sel && (
        <div style={{ marginTop: '10px', fontSize: '12px' }}>
          <div style={{ fontWeight: 700, marginBottom: '4px' }}>{month.toLocaleDateString('en-US', { month: 'short' })} {sel.d}</div>
          {sel.list.map((r) => {
            const m = rentalMath(r, r.endOdoMi ?? odometerMi);
            return <div key={r.id} style={{ padding: '4px 0', color: '#334155' }}>🚙 {r.renter} · {m.tDays}d · {money(m.total)} · {r.status}</div>;
          })}
        </div>
      )}
    </div>
  );
}

// vehicle-condition photo slots captured per rental
const CONDITION_SLOTS = [['front', 'Front'], ['left', 'Left'], ['right', 'Right'], ['back', 'Back'], ['inside0', 'Inside 1'], ['inside1', 'Inside 2'], ['inside2', 'Inside 3'], ['inside3', 'Inside 4']];

// Rental agreement: license photo + number, contract terms & liability, the
// renter's signature, and this rental's own condition photos. Signed in person
// here (or via the copied link). Files are stored per-rental; the PDF pulls them
// together. Condition photos also refresh the car profile photos.
function AgreementBlock({ rental, deviceId, ctx, onPrint }) {
  const [open, setOpen] = useState(true);
  const [showTerms, setShowTerms] = useState(false);
  const [license, setLicense] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [sig, setSig] = useState(null);
  const [rphotos, setRphotos] = useState({});
  const [licenseNo, setLicenseNo] = useState(rental.renterLicense || '');
  const [signedAt, setSignedAt] = useState(rental.signedAt || null);
  const [dnr, setDnr] = useState(() => isOnDoNotRent(rental.renterLicense));

  useEffect(() => {
    let live = true;
    getRentalFile(rental.id, 'license').then((d) => { if (live) setLicense(d || null); }).catch(() => {});
    getRentalFile(rental.id, 'customer').then((d) => { if (live) setCustomer(d || null); }).catch(() => {});
    getRentalFile(rental.id, 'signature').then((d) => { if (live) setSig(d || null); }).catch(() => {});
    getRentalPhotos(rental.id).then((p) => { if (live) setRphotos(p || {}); }).catch(() => {});
    return () => { live = false; };
  }, [rental.id]);

  const onCustomer = async (dataUrl) => { try { await saveRentalFile(rental.id, 'customer', dataUrl); setCustomer(dataUrl); } catch { /* ignore */ } };

  const onConditionPhoto = async (slot, dataUrl) => {
    try {
      await saveRentalPhoto(rental.id, slot, dataUrl); // this rental's own copy ONLY
      // NOTE: do NOT touch the car profile photos — those only change when the
      // customer uploads new ones on the vehicle profile itself.
      setRphotos((p) => ({ ...p, [slot]: dataUrl }));
    } catch { /* ignore */ }
  };

  const onLicense = async (dataUrl) => { try { await saveRentalFile(rental.id, 'license', dataUrl); setLicense(dataUrl); } catch { /* ignore */ } };
  const onSig = async (dataUrl) => { setSig(dataUrl || null); try { await saveRentalFile(rental.id, 'signature', dataUrl || ''); } catch { /* ignore */ } };

  const saveSigned = () => {
    if (!sig) { alert('Have the renter sign in the box first.'); return; }
    const when = new Date().toISOString();
    updateRental(rental.id, { renterLicense: licenseNo, signedAt: when, contractSnapshot: contractPlainText({ ...ctx, license: licenseNo }) });
    setSignedAt(when);
    alert('✅ Signed rental agreement saved.');
  };
  const copyLink = () => {
    const link = `${window.location.origin}/sign/${rental.id}`;
    try { if (navigator.clipboard) navigator.clipboard.writeText(link); } catch { /* ignore */ }
    alert(`Renter sign link copied:\n${link}\n\nOpen it on the renter's phone to review and sign. In-person signing here works now; cross-device link signing syncs once the backend is connected.`);
  };

  return (
    <div style={{ background: '#eef2f7', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px', marginBottom: '8px' }}>
      <div onClick={() => setOpen((o) => !o)} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
        <span style={{ fontWeight: 800, fontSize: '13px', color: '#0f172a', flex: 1 }}>📝 Rental agreement {signedAt ? <span style={{ color: '#16a34a', fontSize: '11px' }}>· ✅ signed {new Date(signedAt).toLocaleDateString()}</span> : <span style={{ color: '#b45309', fontSize: '11px' }}>· not signed</span>}</span>
        <span style={{ color: '#94a3b8' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ marginTop: '10px' }}>
          {dnr && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px', marginBottom: '10px', color: '#b91c1c', fontSize: '12px', fontWeight: 700 }}>
              🚫 DO NOT RENT — this license{dnr.name ? ` (${dnr.name})` : ''} is on the Do Not Rent list.{dnr.reason ? ` Reason: ${dnr.reason}` : ''} The rental cannot be started.
            </div>
          )}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '8px', alignItems: 'flex-end' }}>
            <div style={{ flex: 1, minWidth: '150px' }}>
              <span style={lbl}>Driver's license #</span>
              <input style={{ ...inp, borderColor: dnr ? '#dc2626' : inp.border }} value={licenseNo} onChange={(e) => { setLicenseNo(e.target.value); setDnr(isOnDoNotRent(e.target.value)); }} onBlur={() => updateRental(rental.id, { renterLicense: licenseNo })} placeholder="License number" />
            </div>
            <div>
              <span style={lbl}>Customer + vehicle</span>
              <PhotoPicker facing="user" onPhoto={onCustomer} onFileProcess={fileToResizedDataUrl}>
                {customer
                  ? <img src={customer} alt="customer with vehicle" style={{ height: '46px', borderRadius: '6px', border: '1px solid #cbd5e1', display: 'block' }} />
                  : <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: '46px', width: '84px', border: '1px dashed #cbd5e1', borderRadius: '6px', fontSize: '10px', color: '#64748b', textAlign: 'center', lineHeight: 1.1 }}>🧍📷 Renter by car</span>}
              </PhotoPicker>
            </div>
          </div>

          <span style={lbl}>Vehicle condition photos (this rental)</span>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', margin: '4px 0 2px' }}>
            {CONDITION_SLOTS.map(([slot, label]) => (
              <PhotoPicker key={slot} onPhoto={(d) => onConditionPhoto(slot, d)} onFileProcess={fileToResizedDataUrl}>
                {rphotos[slot]
                  ? <img src={rphotos[slot]} alt={label} style={{ width: '62px', height: '48px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #cbd5e1', display: 'block' }} />
                  : <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '62px', height: '48px', border: '1px dashed #cbd5e1', borderRadius: '6px', fontSize: '9px', color: '#64748b', textAlign: 'center', lineHeight: 1.1 }}>📷 {label}</span>}
              </PhotoPicker>
            ))}
          </div>
          <div style={{ fontSize: '10px', color: '#94a3b8', marginBottom: '10px' }}>Saved to this rental only. The car profile photos are separate and unaffected.</div>

          <button onClick={() => setShowTerms((s) => !s)} style={{ ...btn('#f1f5f9', '#334155'), padding: '5px 10px', fontSize: '11px', marginBottom: '8px' }}>{showTerms ? 'Hide' : 'View'} agreement terms</button>
          {showTerms && (
            <div style={{ maxHeight: '180px', overflowY: 'auto', background: '#e2e8f0', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px', fontSize: '11px', color: '#334155', marginBottom: '8px' }}>
              <div style={{ fontWeight: 800, marginBottom: '6px' }}>Renter responsibility &amp; liability</div>
              {liabilityClauses(ctx).map(([h, t], i) => (
                <div key={i} style={{ marginBottom: '6px' }}><b>{i + 1}. {h}.</b> {t}</div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div>
              <span style={lbl}>Driver's license photo</span>
              <PhotoPicker onPhoto={onLicense} onFileProcess={fileToResizedDataUrl}>
                {license
                  ? <img src={license} alt="license" style={{ width: '120px', height: '130px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #cbd5e1', display: 'block' }} />
                  : <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '120px', height: '130px', border: '1px dashed #cbd5e1', borderRadius: '8px', fontSize: '11px', color: '#64748b', textAlign: 'center' }}>📷 License</span>}
              </PhotoPicker>
            </div>
            <div style={{ flex: 1, minWidth: '220px' }}>
              <span style={lbl}>Renter signature</span>
              <SignaturePad value={sig} onChange={onSig} height={130} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
            <button onClick={saveSigned} style={btn('#16a34a', 'white')}>✅ Save signed agreement</button>
            <button onClick={copyLink} style={btn('#eef2ff', '#1d4ed8')}>🔗 Copy sign link</button>
            <button onClick={onPrint} style={btn('#0f172a', 'white')}>🖨️ Print / Save PDF</button>
          </div>
          <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '6px' }}>Renter accepts liability for the vehicle, tickets, tolls, and all charges. Sign in person here, or send the link. The PDF includes the signed contract, license, and car photos.</div>
        </div>
      )}
    </div>
  );
}

// Draft setup: fill everything (renter info, pricing, license, photos, customer
// photo, signature) BEFORE the clock starts. Save Draft keeps it to finish later;
// Start rental validates the essentials and begins the 24-hour clock.
function RentalSetup({ deviceId, draftId, liveOdo, odometerMi, buildCtx, printPdf, sendPaymentLink, onClose }) {
  const rec0 = getRentals(deviceId).find((r) => r.id === draftId) || {};
  const [payAmount, setPayAmount] = useState('');
  const [payReason, setPayReason] = useState('');
  const [showLink, setShowLink] = useState(false); // reveal amount/email only for a link
  const [linkEmail, setLinkEmail] = useState('');   // auto-filled from renter email, editable
  const [cashPaid, setCashPaid] = useState(false);  // show the "cash" indicator
  const [f, setF] = useState({
    renter: rec0.renter || '', phone: rec0.phone || '', email: rec0.email || '',
    days: rec0.days != null ? rec0.days : 1, dailyPrice: rec0.dailyPrice != null ? rec0.dailyPrice : '',
    includedMilesPerDay: rec0.includedMilesPerDay != null ? rec0.includedMilesPerDay : '', overageRate: rec0.overageRate != null ? rec0.overageRate : '',
    deposit: rec0.deposit != null ? rec0.deposit : '', paid: rec0.paid != null ? rec0.paid : '',
    tollRate: rec0.tollRate != null ? rec0.tollRate : 0.5,
    startOdoMi: rec0.startOdoMi != null ? rec0.startOdoMi : (liveOdo !== '' ? liveOdo : ''),
  });
  const [backdate, setBackdate] = useState('');

  const set = (patch) => {
    const nf = { ...f, ...patch }; setF(nf);
    updateRental(draftId, {
      renter: nf.renter, phone: nf.phone, email: nf.email,
      days: Number(nf.days) || 0, dailyPrice: Number(nf.dailyPrice) || 0,
      includedMilesPerDay: Number(nf.includedMilesPerDay) || 0, overageRate: Number(nf.overageRate) || 0,
      deposit: Number(nf.deposit) || 0, paid: Number(nf.paid) || 0,
      tollRate: nf.tollRate === '' ? 0 : Number(nf.tollRate),
      startOdoMi: nf.startOdoMi === '' ? null : Number(nf.startOdoMi),
    });
  };

  // provisional record (clock = now) for the agreement preview / PDF
  const preview = { ...(getRentals(deviceId).find((r) => r.id === draftId) || rec0), startAt: new Date().toISOString() };
  const m = rentalMath(preview, odometerMi);
  const ctx = buildCtx(preview, m);

  const fetchOdoAt = async (whenLocal) => {
    try {
      const when = new Date(whenLocal).getTime();
      const route = await getRouteReport(deviceId, new Date(when - 3600000).toISOString(), new Date(when + 6 * 3600000).toISOString());
      let best = null; let bestDiff = Infinity;
      (route || []).forEach((pos) => {
        const o = odoMilesFromAttrs(pos.attributes || {});
        if (o == null) return;
        const diff = Math.abs(new Date(pos.fixTime || pos.deviceTime).getTime() - when);
        if (diff < bestDiff) { bestDiff = diff; best = o; }
      });
      return best != null ? Math.round(best) : null;
    } catch { return null; }
  };

  const validate = async () => {
    const cur = getRentals(deviceId).find((r) => r.id === draftId) || {};
    if (!f.renter.trim()) { alert('Enter the renter name.'); return false; }
    if (!(cur.renterLicense || '').trim()) { alert("Enter the renter's driver's license number in the agreement below."); return false; }
    // DO NOT RENT check — block if this license is flagged
    const dnr = isOnDoNotRent(cur.renterLicense);
    if (dnr) {
      alert(`🚫 DO NOT RENT\n\nLicense ${cur.renterLicense}${dnr.name ? ` (${dnr.name})` : ''} is on the Do Not Rent list.${dnr.reason ? `\nReason: ${dnr.reason}` : ''}\n\nThis rental cannot be started.`);
      return false;
    }
    const sig = await getRentalFile(draftId, 'signature').catch(() => null);
    if (!sig) { alert('The renter must sign the agreement before the rental can start.'); return false; }
    return true;
  };

  // Confirm the rental was paid in full IN CASH. This is an explicit click (not
  // triggered by typing an amount): it marks paid = total, records the remaining
  // balance as a cash payment, and stamps the method as cash.
  const markCash = () => {
    const total = m.total;
    const already = Number(f.paid) || 0;
    const addAmt = Math.max(0, Math.round(total - already)); // remaining balance to log as cash
    const cur = getRentals(deviceId).find((r) => r.id === draftId) || {};
    const payments = [...(cur.payments || [])];
    if (addAmt > 0) payments.push({ amount: addAmt, method: 'cash', at: new Date().toISOString(), note: payReason });
    setF((nf) => ({ ...nf, paid: total }));
    updateRental(draftId, { paid: total, payments });
    setCashPaid(true);
  };

  const doActivate = async (startAt) => {
    if (!(await validate())) return;
    let startOdoMi = f.startOdoMi !== '' ? Number(f.startOdoMi) : (liveOdo !== '' ? liveOdo : null);
    if (startAt && startOdoMi == null) startOdoMi = await fetchOdoAt(startAt);
    const cur = getRentals(deviceId).find((r) => r.id === draftId) || {};
    updateRental(draftId, { startOdoMi, signedAt: cur.signedAt || new Date().toISOString() });
    activateRental(draftId, startAt || undefined);
    // assign the renter as the car's driver for the duration of the rental
    setAssignedDriver(deviceId, cur.renter || '', cur.renterLicense || '');
    onClose();
  };

  return (
    <div style={{ background: '#eef2f7', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
        <div style={{ fontWeight: 800, fontSize: '14px', color: '#0f172a', flex: 1 }}>🚗 New rental</div>
        <span style={{ fontSize: '10px', fontWeight: 800, color: '#7c3aed', background: '#f5f3ff', borderRadius: '999px', padding: '2px 8px' }}>DRAFT · clock not started</span>
      </div>
      <div style={{ fontSize: '11px', color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '8px 10px', marginBottom: '10px' }}>
        Fill the info, capture the renter's license + photos, and have them sign below. <b>Save draft</b> to finish at pickup, or <b>Start rental</b> to begin the 24-hour clock now.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
        <div><span style={lbl}>Renter name</span><input style={inp} value={f.renter} onChange={(e) => set({ renter: e.target.value })} /></div>
        <div><span style={lbl}>Phone</span><input style={inp} value={f.phone} onChange={(e) => set({ phone: e.target.value })} /></div>
        <div><span style={lbl}>Renter email</span><input style={inp} type="email" value={f.email} onChange={(e) => set({ email: e.target.value })} placeholder="renter@email.com" /></div>
        <div><span style={lbl}>Days out</span><input style={inp} type="number" value={f.days} onChange={(e) => set({ days: e.target.value })} /></div>
        <div><span style={lbl}>$/day</span><input style={inp} type="number" value={f.dailyPrice} onChange={(e) => set({ dailyPrice: e.target.value })} /></div>
        <div><span style={lbl}>Incl. miles/day</span><input style={inp} type="number" value={f.includedMilesPerDay} onChange={(e) => set({ includedMilesPerDay: e.target.value })} /></div>
        <div><span style={lbl}>Overage $/mi</span><input style={inp} type="number" step="0.01" value={f.overageRate} onChange={(e) => set({ overageRate: e.target.value })} /></div>
        <div><span style={lbl}>Toll rate ($/toll)</span><input style={inp} type="number" step="0.01" value={f.tollRate} onChange={(e) => set({ tollRate: e.target.value })} /></div>
        <div><span style={lbl}>Deposit held ($)</span><input style={inp} type="number" value={f.deposit} onChange={(e) => set({ deposit: e.target.value })} /></div>
        <div><span style={lbl}>Start odometer (mi){liveOdo !== '' && ' · from car'}</span><input style={inp} type="number" placeholder={liveOdo !== '' ? String(liveOdo) : 'enter manually'} value={f.startOdoMi} onChange={(e) => set({ startOdoMi: e.target.value })} /></div>
      </div>
      {liveOdo !== '' && <div style={{ fontSize: '11px', color: '#16a34a', marginBottom: '8px' }}>📟 Odometer read from the car: {liveOdo.toLocaleString()} mi.</div>}

      {/* payment — collect up front: record cash or send a payment link */}
      {(() => {
        const total = m.total; const paid = Number(f.paid) || 0; const bal = Math.max(0, total - paid);
        return (
          <div style={{ background: '#e2e8f0', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px', marginBottom: '10px' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '8px' }}>
              <div style={{ width: '110px' }}><span style={lbl}>Paid so far ($)</span><input style={inp} type="number" value={f.paid} onChange={(e) => { set({ paid: e.target.value }); setCashPaid(false); }} /></div>
              <div style={{ flex: 1, minWidth: '150px', fontSize: '12px' }}>
                {bal > 0
                  ? <span style={{ color: '#b45309', fontWeight: 800 }}>Balance due {money(bal)}</span>
                  : cashPaid
                    ? <span style={{ color: '#16a34a', fontWeight: 800 }}>✅ Paid in full · cash 💵</span>
                    : <span style={{ color: '#0f766e', fontWeight: 800 }}>Balance $0 — confirm how it was paid ↓</span>}
                <div style={{ color: '#94a3b8' }}>Rental total {money(total)}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button onClick={markCash} disabled={cashPaid} style={btn(!cashPaid ? '#16a34a' : '#e2e8f0', !cashPaid ? 'white' : '#94a3b8')}>💵 Mark paid in full (cash)</button>
              <button onClick={() => { setShowLink((s) => { const ns = !s; if (ns && !linkEmail) setLinkEmail(f.email || ''); return ns; }); }} style={btn('#7c3aed', 'white')}>💳 {showLink ? 'Hide link options' : 'Send payment link'}</button>
            </div>

            {showLink && (
              <div style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: '8px', padding: '10px', marginTop: '8px' }}>
                <div style={{ marginBottom: '8px' }}>
                  <span style={lbl}>Payment note (optional — shows on the invoice)</span>
                  <textarea style={{ ...inp, minHeight: '38px' }} value={payReason} onChange={(e) => setPayReason(e.target.value)} placeholder="e.g. deposit + first day" />
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ width: '100px' }}><span style={lbl}>Amount ($)</span><input style={inp} type="number" value={payAmount !== '' ? payAmount : Math.round(bal)} onChange={(e) => setPayAmount(e.target.value)} /></div>
                  <div style={{ flex: 1, minWidth: '160px' }}><span style={lbl}>Send to email</span><input style={inp} type="email" value={linkEmail} onChange={(e) => setLinkEmail(e.target.value)} placeholder="renter@email.com" /></div>
                  <button onClick={() => { const rec = getRentals(deviceId).find((r) => r.id === draftId) || {}; if (linkEmail) updateRental(draftId, { email: linkEmail }); sendPaymentLink({ ...rec, email: linkEmail }, payAmount !== '' ? payAmount : Math.round(bal), payReason, m); }} style={btn('#7c3aed', 'white')}>✉️ Send invoice + link</button>
                </div>
              </div>
            )}
            <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '6px' }}>Collect the deposit + rental up front. “Start rental” only begins the clock.</div>
          </div>
        );
      })()}

      {/* the agreement: license, condition photos, customer photo, signature */}
      <AgreementBlock rental={preview} deviceId={deviceId} ctx={ctx} onPrint={() => printPdf(getRentals(deviceId).find((r) => r.id === draftId) || preview)} />

      {/* optional backdated start */}
      <div style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: '8px', padding: '10px', margin: '8px 0' }}>
        <span style={lbl}>📅 Backdated start (optional)</span>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <input style={{ ...inp, flex: 1, minWidth: '180px' }} type="datetime-local" value={backdate} onChange={(e) => setBackdate(e.target.value)} />
          <button disabled={!backdate} onClick={() => doActivate(backdate)} style={btn(backdate ? '#7c3aed' : '#e2e8f0', backdate ? 'white' : '#94a3b8')}>▶ Start on this date</button>
        </div>
        <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>Use if the rental already began earlier — the clock and days bill from that date, and the start odometer is read from the car's history at that time.</div>
      </div>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button onClick={onClose} style={btn('#e2e8f0', '#334155')}>💾 Save draft &amp; close</button>
        <button onClick={() => doActivate(null)} style={btn('linear-gradient(135deg,#0066ff,#0047d0)', 'white')}>▶ Start rental now (begins clock)</button>
        <button onClick={() => { if (window.confirm('Discard this draft?')) { deleteRental(draftId); onClose(); } }} style={btn('#fef2f2', '#b91c1c')}>🗑 Discard</button>
      </div>
    </div>
  );
}

// ISO → value for a <input type="datetime-local">
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso); const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

// Per-car rental history: every past and active rental, expandable to full detail
// with car photos, an editable form for closed rentals, license/signature capture,
// and a one-click printable/savable PDF for each.
function RentalHistory({ deviceId, odometerMi, liveMiles, onPrint, buildCtx, actor }) {
  const [, force] = useState(0);
  const refresh = () => force((n) => n + 1);
  const rentals = getRentals(deviceId, actor);
  const [openId, setOpenId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState({});
  const [photos, setPhotos] = useState({});
  const [openPhotos, setOpenPhotos] = useState({});
  useEffect(() => { getVehiclePhotos(deviceId).then(setPhotos).catch(() => {}); }, [deviceId]);
  useEffect(() => {
    if (!openId) { setOpenPhotos({}); return undefined; }
    let live = true;
    getRentalPhotos(openId).then((p) => { if (live) setOpenPhotos(p || {}); }).catch(() => {});
    return () => { live = false; };
  }, [openId]);

  if (!rentals.length) {
    return <div style={{ background: '#eef2f7', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px', fontSize: '13px', color: '#64748b' }}>No rentals yet for this vehicle. Start one on the Rental tab.</div>;
  }

  const beginEdit = (r) => {
    setDraft({
      renter: r.renter || '', phone: r.phone || '', email: r.email || '', renterLicense: r.renterLicense || '',
      days: r.days ?? '', dailyPrice: r.dailyPrice ?? '', includedMilesPerDay: r.includedMilesPerDay ?? '', overageRate: r.overageRate ?? '',
      paid: r.paid ?? '', deposit: r.deposit ?? '', depositReturned: r.depositReturned ?? '', depositReason: r.depositReason || '', chargeReason: r.chargeReason || '',
      milesDriven: r.milesDriven ?? '', startOdoMi: r.startOdoMi ?? '', endOdoMi: r.endOdoMi ?? '',
      startAt: toLocalInput(r.startAt), closedAt: toLocalInput(r.closedAt), notes: r.notes || '',
    });
    setEditId(r.id);
  };
  const saveEdit = (r) => {
    const num = (v) => (v === '' || v == null ? null : Number(v));
    updateRental(r.id, {
      renter: draft.renter, phone: draft.phone, email: draft.email, renterLicense: draft.renterLicense,
      days: Number(draft.days) || 0, dailyPrice: Number(draft.dailyPrice) || 0,
      includedMilesPerDay: Number(draft.includedMilesPerDay) || 0, overageRate: Number(draft.overageRate) || 0,
      paid: Number(draft.paid) || 0, deposit: Number(draft.deposit) || 0,
      depositReturned: num(draft.depositReturned), depositReason: draft.depositReason, chargeReason: draft.chargeReason,
      milesDriven: draft.milesDriven === '' ? undefined : Number(draft.milesDriven),
      startOdoMi: num(draft.startOdoMi), endOdoMi: num(draft.endOdoMi), notes: draft.notes,
      startAt: draft.startAt ? new Date(draft.startAt).toISOString() : r.startAt,
      closedAt: draft.closedAt ? new Date(draft.closedAt).toISOString() : r.closedAt,
    });
    setEditId(null); refresh();
  };
  const F = ({ label, k, type, step, wide }) => (
    <div style={{ width: wide ? '100%' : '108px' }}>
      <span style={lbl}>{label}</span>
      <input style={inp} type={type || 'text'} step={step} value={draft[k]} onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))} />
    </div>
  );

  return (
    <div style={{ background: '#eef2f7', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px' }}>
      <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '8px' }}>All rentals for this vehicle ({rentals.length})</div>
      {rentals.map((r) => {
        const m = rentalMath(r, r.status === 'closed' ? r.endOdoMi : odometerMi, r.status === 'active' ? liveMiles : undefined);
        const isOpen = openId === r.id;
        const isActive = r.status === 'active';
        const editing = editId === r.id;
        return (
          <div key={r.id} style={{ border: '1px solid #eef2f7', borderRadius: '8px', marginBottom: '6px', overflow: 'hidden' }}>
            <div onClick={() => { setOpenId(isOpen ? null : r.id); if (editing) setEditId(null); }} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', cursor: 'pointer', background: isActive ? '#eff6ff' : '#fff' }}>
              <span style={{ fontWeight: 800, color: '#0f172a', fontSize: '13px' }}>{r.renter || 'Rental'}</span>
              <span style={{ fontSize: '10px', fontWeight: 800, padding: '2px 8px', borderRadius: '999px', background: isActive ? '#dbeafe' : '#f1f5f9', color: isActive ? '#1d4ed8' : '#64748b' }}>{isActive ? (m.overdue ? 'OVERDUE' : 'ACTIVE') : 'CLOSED'}</span>
              <span style={{ fontSize: '12px', color: '#94a3b8' }}>{new Date(r.startAt).toLocaleDateString()} · {m.tDays}d · {m.milesDriven != null ? `${Math.round(m.milesDriven)}mi` : '—'}</span>
              <span style={{ marginLeft: 'auto', fontWeight: 800, color: '#16a34a' }}>{money(m.total)}</span>
              <span style={{ color: '#94a3b8' }}>{isOpen ? '▲' : '▼'}</span>
            </div>
            {isOpen && (
              <div style={{ padding: '10px', borderTop: '1px solid #eef2f7', fontSize: '12px', color: '#334155' }}>
                {editing ? (
                  <div>
                    <div style={{ fontWeight: 800, marginBottom: '8px', color: '#0f172a' }}>Edit rental</div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                      {F({ label: 'Renter', k: 'renter' })}
                      {F({ label: 'Phone', k: 'phone' })}
                      {F({ label: 'Email', k: 'email', type: 'email' })}
                      {F({ label: 'License #', k: 'renterLicense' })}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                      {F({ label: 'Days', k: 'days', type: 'number' })}
                      {F({ label: '$/day', k: 'dailyPrice', type: 'number' })}
                      {F({ label: 'Incl mi/day', k: 'includedMilesPerDay', type: 'number' })}
                      {F({ label: 'Overage $/mi', k: 'overageRate', type: 'number', step: '0.01' })}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                      {F({ label: 'Miles driven', k: 'milesDriven', type: 'number' })}
                      {F({ label: 'Start odo', k: 'startOdoMi', type: 'number' })}
                      {F({ label: 'End odo', k: 'endOdoMi', type: 'number' })}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                      {F({ label: 'Paid ($)', k: 'paid', type: 'number' })}
                      {F({ label: 'Deposit ($)', k: 'deposit', type: 'number' })}
                      {F({ label: 'Deposit returned ($)', k: 'depositReturned', type: 'number' })}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                      <div style={{ flex: 1, minWidth: '170px' }}><span style={lbl}>Start date/time</span><input style={inp} type="datetime-local" value={draft.startAt} onChange={(e) => setDraft((d) => ({ ...d, startAt: e.target.value }))} /></div>
                      <div style={{ flex: 1, minWidth: '170px' }}><span style={lbl}>Return date/time</span><input style={inp} type="datetime-local" value={draft.closedAt} onChange={(e) => setDraft((d) => ({ ...d, closedAt: e.target.value }))} /></div>
                    </div>
                    <div style={{ marginBottom: '8px' }}><span style={lbl}>Deposit deduction reason</span><textarea style={{ ...inp, minHeight: '38px' }} value={draft.depositReason} onChange={(e) => setDraft((d) => ({ ...d, depositReason: e.target.value }))} /></div>
                    <div style={{ marginBottom: '8px' }}><span style={lbl}>Charge / balance reason</span><textarea style={{ ...inp, minHeight: '38px' }} value={draft.chargeReason} onChange={(e) => setDraft((d) => ({ ...d, chargeReason: e.target.value }))} /></div>
                    <div style={{ marginBottom: '8px' }}><span style={lbl}>Notes</span><textarea style={{ ...inp, minHeight: '38px' }} value={draft.notes} onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))} /></div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => setEditId(null)} style={btn('#e2e8f0', '#334155')}>Cancel</button>
                      <button onClick={() => saveEdit(r)} style={btn('#16a34a', 'white')}>✓ Save changes</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ marginBottom: '4px' }}>📅 {new Date(r.startAt).toLocaleString()} → {new Date(m.dueAt).toLocaleString()}{r.closedAt ? ` · returned ${new Date(r.closedAt).toLocaleString()}` : ''}</div>
                    <div style={{ marginBottom: '4px' }}>🛣️ {m.milesDriven != null ? `${Math.round(m.milesDriven)} of ${m.allowedMiles} mi${m.overageMiles ? ` · ${Math.round(m.overageMiles)} over (${money(m.overageCharge)})` : ''}` : `${m.allowedMiles} mi allowance`}</div>
                    <div style={{ marginBottom: '4px' }}>💵 Total {money(m.total)} · paid {money(m.paid)} · {m.balanceDue > 0 ? <b style={{ color: '#b45309' }}>balance {money(m.balanceDue)}</b> : <b style={{ color: '#16a34a' }}>paid in full</b>}</div>
                    {r.deposit ? <div style={{ marginBottom: '4px' }}>🔒 Deposit {money(r.deposit)}{r.depositReturned != null ? ` · returned ${money(r.depositReturned)}` : ''}{r.depositReason ? ` — ${r.depositReason}` : ''}</div> : null}
                    <div style={{ marginBottom: '8px' }}>{r.signedAt ? <span style={{ color: '#16a34a', fontWeight: 700 }}>✅ Agreement signed {new Date(r.signedAt).toLocaleDateString()}</span> : <span style={{ color: '#b45309' }}>Agreement not signed</span>}</div>
                    {(() => {
                      const merged = {};
                      ['front', 'left', 'right', 'back', 'inside0', 'inside1', 'inside2', 'inside3'].forEach((s) => { merged[s] = openPhotos[s] || photos[s]; });
                      const list = Object.keys(merged).filter((s) => merged[s]);
                      return list.length ? (
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
                          {list.map((s) => <img key={s} src={merged[s]} alt={s} style={{ width: '64px', height: '48px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e2e8f0' }} />)}
                        </div>
                      ) : null;
                    })()}
                    {/* capture/replace license + signature even for closed rentals */}
                    {buildCtx && <AgreementBlock rental={r} deviceId={deviceId} ctx={buildCtx(r, m)} onPrint={() => onPrint(r)} />}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button onClick={() => beginEdit(r)} style={btn('#eef2ff', '#1d4ed8')}>✏️ Edit</button>
                      <button onClick={() => onPrint(r)} style={btn('#0f172a', 'white')}>🖨️ Print / Save PDF</button>
                      <button onClick={() => {
                        const lic = r.renterLicense || window.prompt("Driver's license # to flag as Do Not Rent:", '');
                        if (!lic) return;
                        const reason = window.prompt(`Reason for flagging ${r.renter || lic} (optional):`, '') || '';
                        addDoNotRent({ license: lic, name: r.renter || '', reason });
                        alert(`🚫 ${r.renter || lic} added to the Do Not Rent list.`);
                      }} style={btn('#fff7ed', '#b45309')}>🚫 Do Not Rent</button>
                      <button onClick={() => { if (window.confirm('Delete this rental record?')) { deleteRental(r.id); refresh(); } }} style={btn('#fef2f2', '#b91c1c')}>🗑 Delete</button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
